import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';

import SEO from '@/components/SEO';
import { indianArtStarsBySlug } from '@/lib/indianArtStars';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const sectionList = (title, items) => (
  <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
    <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#FF6F00]">{title}</p>
    <div className="mt-4 grid gap-3">
      {items.map((item) => (
        <div key={item} className="rounded-[1.35rem] bg-[#f8fafc] px-4 py-4 text-sm font-semibold leading-7 text-gray-700">
          {item}
        </div>
      ))}
    </div>
  </section>
);

export default function IndianArtStarProfile() {
  const { slug } = useParams();
  const fallbackStar = indianArtStarsBySlug[slug];
  const [remoteStar, setRemoteStar] = useState(null);
  const [loading, setLoading] = useState(true);
  const star = remoteStar || fallbackStar;
  const publishedArtworks = useMemo(
    () =>
      (star?.artworks || [])
        .filter((artwork) => artwork?.src && artwork?.is_published !== false)
        .sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0)),
    [star],
  );
  const featuredArtworks = publishedArtworks.slice(0, 4);
  const hasArtworks = publishedArtworks.length > 0;
  const hasVideos = (star?.portfolioVideoUrls || []).length > 0;

  useEffect(() => {
    let mounted = true;
    const loadStar = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/v1/indian-art-stars/${slug}`);
        if (mounted) {
          setRemoteStar(response.data?.star || null);
        }
      } catch (error) {
        if (mounted) {
          setRemoteStar(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    loadStar();
    return () => {
      mounted = false;
    };
  }, [slug]);

  const keywords = useMemo(() => [
    star?.name,
    star?.knownAs,
    star?.city,
    star?.state,
    star?.category,
    star?.specialty,
    `${star?.name || ''} ${star?.category || ''}`.trim(),
    `${star?.name || ''} ${star?.specialty || ''}`.trim(),
    `${star?.city || ''} ${star?.category || ''}`.trim(),
    `${star?.state || ''} ${star?.category || ''}`.trim(),
    'Stars of Indian Art',
    'KalaManch curated artist',
    'Indian traditional art',
    'Indian heritage artist',
  ].filter(Boolean).join(', '), [star]);

  if (loading && fallbackStar) {
    // Let the fallback render while published API data loads.
  }

  if (!star) {
    return (
      <main className="min-h-screen bg-[#f7f9fc] pt-28">
        <section className="mx-auto max-w-5xl px-4 pb-16 sm:px-6 lg:px-8">
          <div className="rounded-[2rem] border border-orange-100 bg-white p-10 text-center shadow-xl">
            <p className="text-sm font-bold uppercase tracking-[0.24em] text-[#FF6F00]">Stars of Indian Art</p>
            <h1 className="mt-4 text-4xl font-extrabold text-gray-950">Artist feature not available yet</h1>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-8 text-gray-600">
              This curated Indian art page has not been published yet. We are adding more artist stories and gallery pages in phases.
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Link to="/indian-art" className="btn-primary">Back to Indian Art</Link>
              <Link to="/artists" className="btn-outline">Browse Artists</Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  const title = `${star.name} | ${star.heading} | KalaManch.art`;
  const description = `${star.name}, ${star.specialty}, featured by KalaManch in Stars of Indian Art. Explore artworks, profile highlights, exhibitions, and curated artist details from ${star.regionLabel}.`;

  return (
    <main className="min-h-screen bg-[#f7f9fc] pt-28">
      <SEO
        title={title}
        description={description}
        keywords={keywords}
        image={star.coverArtwork || star.portrait}
        type="profile"
      />

      <section className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-[2rem] border border-orange-100 bg-white shadow-xl">
          <div className="grid gap-0 lg:grid-cols-[0.95fr_1.05fr]">
            <div className="bg-gradient-to-br from-[#fff7ed] via-white to-[#ecfeff] px-6 py-8 sm:px-8 lg:px-10">
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#FF6F00]">{star.eyebrow}</p>
              <h1 className="mt-4 text-4xl font-extrabold leading-tight text-gray-950 sm:text-5xl">
                {star.name}
              </h1>
              <p className="mt-3 text-xl font-bold text-[#4A148C]">{star.specialty}</p>
              <p className="mt-4 max-w-2xl text-base leading-8 text-gray-700">{star.shortIntro}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {star.heroBadges.map((badge) => (
                  <span
                    key={badge}
                    className="rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-bold text-emerald-700"
                  >
                    {badge}
                  </span>
                ))}
              </div>
                  <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link to={star.profilePath} className="btn-primary">
                  Open KalaManch Profile
                </Link>
                {star.externalWebsite && (
                  <a
                    href={star.externalWebsite}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-outline"
                  >
                    Artist Website
                  </a>
                )}
                {star.secondaryWebsite && (
                  <a
                    href={star.secondaryWebsite}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-outline"
                  >
                    More Artist Links
                  </a>
                )}
              </div>
            </div>

            <div className="bg-[#07111F] px-5 py-5 sm:px-6">
              <div className="grid gap-4 sm:grid-cols-2">
                {hasArtworks ? featuredArtworks.map((artwork) => (
                  <figure key={artwork.src} className="overflow-hidden rounded-[1.35rem] border border-white/10 bg-white/5">
                    <img
                      src={artwork.src}
                      alt={artwork.title}
                      className="aspect-[4/3] w-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                    <figcaption className="px-4 py-3 text-sm font-semibold text-white/85">{artwork.title}</figcaption>
                  </figure>
                )) : (
                  <div className="sm:col-span-2 rounded-[1.5rem] border border-dashed border-white/20 bg-white/5 p-6 text-white/80">
                    <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#FDBA74]">Gallery in progress</p>
                    <h3 className="mt-3 text-2xl font-extrabold text-white">Approved artwork showcase will grow here</h3>
                    <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-200">
                      KalaManch has opened this curated page for {star.name}. Once approved artwork images are added, this section will expand into a fuller virtual gallery.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[0.8fr_1.2fr]">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            {star.portrait || star.coverArtwork ? (
              <img
                src={star.portrait || star.coverArtwork}
                alt={star.name}
                className="w-full rounded-[1.5rem] bg-[#f8fafc] object-contain"
                loading="eager"
                decoding="async"
              />
            ) : (
              <div className="flex min-h-[18rem] items-center justify-center rounded-[1.5rem] bg-gradient-to-br from-[#fff7ed] via-white to-[#ecfeff] p-8 text-center">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#FF6F00]">Stars of Indian Art</p>
                  <h3 className="mt-3 text-3xl font-extrabold text-slate-950">{star.name}</h3>
                  <p className="mt-2 text-sm font-semibold text-[#4A148C]">{star.specialty}</p>
                </div>
              </div>
            )}
            <div className="mt-5 rounded-[1.4rem] bg-[#f8fafc] p-4">
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#FF6F00]">Curatorial note</p>
              <p className="mt-3 text-sm leading-7 text-gray-700">{star.curatorialNote}</p>
            </div>
          </div>

          <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#00A68C]">{star.heading}</p>
            <h2 className="mt-3 text-3xl font-extrabold text-gray-950">Detailed profile for {star.name}</h2>
            <div className="mt-6 space-y-5 text-base leading-8 text-gray-700">
              {star.bioParagraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {star.profileHighlights.map((item) => (
                <div key={item} className="rounded-[1.35rem] border border-orange-100 bg-orange-50/70 px-4 py-4 text-sm font-semibold leading-7 text-gray-700">
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-2">
          {sectionList('Education and training', star.education)}
          {sectionList('Career and mentoring', star.career)}
          {sectionList('Exhibitions and visibility', star.exhibitions)}
          {sectionList('Recognition and collections', star.recognition)}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#4A148C]">Virtual gallery</p>
              <h2 className="mt-2 text-3xl font-extrabold text-gray-950">Selected works on KalaManch</h2>
            </div>
            <Link to={star.profilePath} className="btn-outline">
              View public profile
            </Link>
          </div>
          <div className="mt-6">
            {hasArtworks ? (
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {publishedArtworks.map((artwork) => (
                  <figure key={artwork.src} className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-[#f8fafc]">
                    <img
                      src={artwork.src}
                      alt={artwork.title}
                      className="aspect-[4/3] w-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                    <figcaption className="px-4 py-3 text-sm font-semibold text-gray-700">{artwork.title}</figcaption>
                  </figure>
                ))}
              </div>
            ) : (
              <div className="rounded-[1.5rem] border border-dashed border-slate-300 bg-slate-50 p-6">
                <p className="text-sm leading-7 text-gray-700">
                  This curated star page is live, and the artwork gallery will be added as KalaManch receives approved image selections for public showcase.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  {star.instagramUrl && (
                    <a href={star.instagramUrl} target="_blank" rel="noreferrer" className="btn-outline">Instagram</a>
                  )}
                  {star.youtubeUrl && (
                    <a href={star.youtubeUrl} target="_blank" rel="noreferrer" className="btn-outline">YouTube</a>
                  )}
                  {star.facebookUrl && (
                    <a href={star.facebookUrl} target="_blank" rel="noreferrer" className="btn-outline">Facebook</a>
                  )}
                </div>
              </div>
            )}
          </div>
          {hasVideos && (
            <div className="mt-8 rounded-[1.5rem] border border-slate-200 bg-[#f8fafc] p-5">
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#4A148C]">Artist videos</p>
              <h3 className="mt-2 text-2xl font-extrabold text-gray-950">Watch the artist in motion</h3>
              <div className="mt-4 flex flex-wrap gap-3">
                {star.portfolioVideoUrls.map((url, index) => (
                  <a
                    key={`${url}-${index}`}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-outline"
                  >
                    Watch video {index + 1}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
